import React from "react";
import {Route, Switch} from "react-router-dom";
import Keycloak from 'keycloak-js';

// CLASSES
import Settings from "@classes/Settings";
import Partner from "@classes/Partner";

// COMPONENTS
import ActivityIndicator from "./components/ActivityIndicator/ActivityIndicator";
import Header from "./components/Header";
import Modal from "./components/Modal/Modal";
import EnvWarning from "./components/EnvWarning";

// CONTEXTS
import MenuDrawerProvider from "@contexts/MenuDrawerProvider";
import StepsDrawerProvider from "@contexts/StepsDrawerProvider";
import NotesDrawerProvider from "@contexts/NotesDrawerProvider";

// HELPERS
import {connectRedux, searchModules, hardReload, delay, initSystemWakeUpEventListener} from "@helpers/utils";
import i18n from "@helpers/i18n";

// ROUTES
import CobRoute from "./routes/Cob/Cob";
import HomeRoute from "./routes/Home/Home";
import PartnerErrorRoute from "./routes/PartnerError/PartnerError";
import PartnerNotFoundRoute from "./routes/PartnerNotFound/PartnerNotFound";

// REDUCERS
import {actions as appActions} from "./reducers/app";
import api from "./helpers/api";

// MUI
import ThemeProvider from "@mui-theme";
import Typography from "@mui/material/Typography";
import SectionHeaderPaper from "@mui-theme/styled/SectionHeaderPaper";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const REFRESH_TIMEOUT_MS = parseInt(import.meta.env.VITE_PAGE_REFRESH_TIMEOUT_MS ?? "1800000", 10); // Default to 30 minutes if not defined in env file
const TIMEOUT_DIALOG_TIME_WINDOW_MS = parseInt(import.meta.env.VITE_TIMEOUT_DIALOG_TIME_WINDOW_MS ?? "21600000", 10); // Default to 6 hours if not defined in env file

const leApp =  connectRedux(state => ({
	router: state.router,
	app: state.app
}), {
	setAppState: appActions.setState
}, class App extends React.PureComponent
{
	state = {
		isLoading: true,
    backgroundStyle: {},
    refreshTimeout: null,
    refreshTimeIsOut: false,
    hiddenStateStartTime: null
	};

	async componentDidMount()
	{
		this._isMounted = true;
		const {history} = this.props;
		const authToken = Settings.get("AUTH_TOKEN");
    const urlParams = new URLSearchParams(window.location.search);

		console.log("Advisory application starting...");

    // Start listening for app visibility changes (app focus state)
    document.addEventListener("visibilitychange", this._handleVisibilityChange);    
    // Start wake from sleep detection, refreshes the app if system wake from sleep is detected
    initSystemWakeUpEventListener(REFRESH_TIMEOUT_MS); // Setting sleep threshold to match refresh timeout, only refreshes if slept for 30 minutes or more
    window.addEventListener("systemwakeup", this._handleSystemWakeUp);


		/**
		 * Map the current domain to a configured partner that contains all application specific settings. Once we create
		 * a new partner we need to enter the domain and mapping in the configuration file (backend project).
		 * If we don't manage to map the domain to a partner we'll show an error page.
		 */
		const partnerDomainMapping = await api("partner/domains");
		const validPartnerDomains = Object.keys(partnerDomainMapping);
		const hostname = window.location.host.toLowerCase();
		let mappedPartnerSite = null;
		validPartnerDomains.forEach(function (partner)
		{
			if (mappedPartnerSite == null && hostname.startsWith(partner))
			{
				mappedPartnerSite = partnerDomainMapping[partner];
			}
		});

		// Handle case when partner mapping hasn't been set up in the app-config.json file
		if (mappedPartnerSite == null)
		{
			Settings.clear();
			this.setState({isLoading: false}, () => history.push("/partner-error"));
			return;
		}   

		// TODO Set up and store partner object based on the mapped partner

		console.log("Loading partner data...");

		const partner = new Partner(mappedPartnerSite);
		try
		{
			await partner.loadData();
		}
		catch (e)
		{
			console.log(e);
			Settings.clear();
			this.setState({isLoading: false}, () => history.push("/partner-error"));
			return;
		}

		const {setAppState} = this.props.actions;
		await setAppState({
      partner: partner,
      isSessionTimeout: false,
      errorModalVisible: false,
      errorModalMessage: null
    });
		console.log("Partner info fetched.");

		/**
		 * Configure the elements of the html document like favicon and document.title
		 */
		partner.setupDocument();    
    
		let backgroundStyle = {};
		if (partner) {      
      const backgroundImage = await import(`./assets/${partner.name}/${partner.backgroundImage}.jpg`);
			backgroundStyle.backgroundImage = `url(${backgroundImage.default})`;
		}
		else {
			backgroundStyle.background = "#EEE";
		}    
    this.setState({backgroundStyle});

		try
		{
			if (partner.faSSO)
			{
				const _this = this;
				const urlParameters = Object.fromEntries(
					new URLSearchParams(window.location.search)
				);


				// init faSSO
				const getSsoRedirectUri = () =>
				{
					const url = new URL(
						`${import.meta.env.VITE_PUBLIC_URL}/keycloak-silent-check-sso.html`,
						window.location.origin
					);
					return url.href;
				};

				const keycloakInitConfig = {
					onLoad: "check-sso",
					silentCheckSsoRedirectUri: getSsoRedirectUri(),
					pkceMethod: "S256",
				};
        
				const keycloak = new Keycloak(`${import.meta.env.VITE_PUBLIC_URL}/keycloak.json`);

				keycloak.init(keycloakInitConfig).then(async function (authenticated)
				{
					if (!authenticated)
					{
						Settings.set("AUTH_TOKEN", "");
						return keycloak.login();
					}

					const {token} = keycloak;
					const profile = await keycloak.loadUserProfile();

					const linkedContactName = profile.attributes.linked_contact_name[0];
					const ssn = linkedContactName.split('(').pop().split(')')[0];

					console.log("FA SSO :: Logging in");
					// Create and set authToken
					const authToken = await api("/auth/tokenSSO", {data: ssn});
					Settings.set("AUTH_TOKEN", authToken.token);

					await setAppState({
						faAuth: {profile, token, activeUserSSN: urlParameters.ssn}
					});

					const lastView = Settings.get("LAST_VIEW") || 1;
					return _this.setState({isLoading: false}, () => history.push("/cob/" + lastView));
				}).catch(function (err)
				{
					console.log(err);
				});
				return;
			}

			if (authToken)
			{
				const lastView = Settings.get("LAST_VIEW") || 1;
				this.setState({isLoading: false}, () => history.push("/cob/" + lastView));
			}
			else
			{
				// Auth token was NOT defined. We need to clear all auth data and redirect the user to the start page
				// where he/she can log in again.
				console.log("Authentication required...");
        Settings.clear(); // Settings clear does not clear SHOW_SESSION_TIMEOUT_MODAL

        const isTimeout = (urlParams.get('sessionTimeout') === "1");
        const showSessionTimeOut = Settings.get("SHOW_SESSION_TIMEOUT_MODAL");

        if (isTimeout && showSessionTimeOut)  // Only shows session timeout dialog if same session and did not exceed time window (see _handleVisibilityChange)
        {          
          await setAppState({
            isSessionTimeout: true,
            errorModalVisible: true,
            errorModalMessage: i18n("general", "session_timeout")
          });
        }
        
        //check if tester to set bankid app accordingly
        const isTester = (urlParams.get('glofitest') === "1");
				this.setState({isLoading: false}, () => history.push(`${isTester? "/?glofitest=1" : "/"}`));
			}
		}
		catch (e)
		{
			console.error(e.message);
			this.setState({isLoading: false});
		}
	}

	componentWillUnmount()
	{
		this._isMounted = false;
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);    
	}

	render()
	{
		const appVersion = import.meta.env.VITE_REACT_APP_VERSION;
		const apiUrl = import.meta.env.VITE_API_URL || "";
		const isProduction = apiUrl === "https://cob.api.fairinvestments.se"
			|| apiUrl === "https://cob.api.citroneer.glofitech.com"
			|| apiUrl === "https://demo.api.glofitech.com";

		const {props, state} = this;
		const {reducers, location} = props;
		const {
      busy,
			user,
			customer,
			customers,
			isSessionTimeout,
			errorModalVisible,
			errorModalForceRestart,
			partner,
			advisor,
			dashboardSelected
		} = reducers.app;
		const errorModalMessage = reducers.app.errorModalMessage ? _convertErrorMessage(reducers.app.errorModalMessage) : null;
		const isHomePage = (location.pathname === "" || location.pathname === "/");
		const isStartPage = (location.pathname === "/cob/1");

		let appContentClassNames = `App-content${user ? " logged-in" : ""}`;
		if (partner && partner.iframed)
		{
			appContentClassNames = appContentClassNames + " iframed";
		}

		if (partner && partner.language)
		{
			Settings.set("LANGUAGE", partner.language);
		}

		if (partner && advisor && (searchModules(partner.modules, "InitSession") || partner.headerWidgets.includes("SearchBar")) && !customers)
		{
			this._getCustomers(advisor.ssn, user.ssn);
		}

		return (
			<ThemeProvider theme={partner?.theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <MenuDrawerProvider>
            <StepsDrawerProvider>
              <NotesDrawerProvider>
                <div id="leApp" className={`App ${isHomePage ? "home" : ""}`}>
                  <div className={isHomePage ? "App-bg" : "App-no-bg"} style={this.state.backgroundStyle}/>
                  <Header
                    user={user}
                    advisor={advisor}
                    customer={customer}
                    customers={customers}
                    location={location}
                    partner={partner}
                    onCurrentAssistantAdvisorClick={this._onCurrentAssistantAdvisorClick}
                  />
                  {!busy && isStartPage && user && (
                    <SectionHeaderPaper>
                      <Typography variant="sectionHeader" align="left">
                        {dashboardSelected !== "start" ?
                          i18n("dashboard_selector", dashboardSelected)
                          :
                          `Välkommen ${user?.firstName} ${user?.lastName}`
                        }
                      </Typography>

                      {dashboardSelected === "start" && user.companyIsPartner === "1" && (
                        <span style={{fontSize: "12px", marginTop: "3px", color: "#95a4ac"}}>
                    {user.companyName}
                  </span>
                      )}
                    </SectionHeaderPaper>
                  )}
                  <div className={appContentClassNames} data-iframe-height>
                    {state.isLoading ? (
                      <div style={{width: "100%", display: "absolute", top:"40vh", left:"-8vw"}}>
                        <ActivityIndicator className="App-ActivityIndicator" color={partner?.theme.colors.primary.main} fixed busy/>
                      </div>
                    ) : (
                      <Switch>
                        <Route path="/partner-error" component={PartnerErrorRoute}/>
                        <Route path="/partner-not-found" component={PartnerNotFoundRoute}/>
                        <Route path="/cob/:view?" component={CobRoute}/>
                        <Route path="/" component={HomeRoute}/>
                      </Switch>
                    )}
                  </div>

                  {errorModalMessage && (
                    <Modal visible={errorModalVisible} title={isSessionTimeout ? "Du har blivit utloggad" : "Ett fel uppstod"} status="danger">
                      {typeof errorModalMessage === "string" ?
                        <p dangerouslySetInnerHTML={{__html: errorModalMessage}}/> : errorModalMessage}

                      <Modal.ButtonsContainer>
                        {errorModalForceRestart && (
                          <Modal.Button label="Starta om" appearance="primary" onClick={this._onRestartModal}/>
                        )}

                        {!errorModalForceRestart && (
                          <Modal.Button label="Stäng" appearance="primary" onClick={this._closeErrorModal}/>
                        )}
                      </Modal.ButtonsContainer>
                    </Modal>
                  )}
                </div>
                {isHomePage && (
                  <div className={`App-Version white`}>
                  <p>{`v${appVersion}`}</p>
                  </div>
                )}
                {!isProduction && (
                  <EnvWarning/>
                )}
              </NotesDrawerProvider>
            </StepsDrawerProvider>
          </MenuDrawerProvider>
        </LocalizationProvider>
      </ThemeProvider>
		);
	}

  _handleVisibilityChange = async () => {
    if (document.visibilityState === "visible") {
      console.log("Visibility state: visible");      
      // Calculate time in hidden state
      const timeInHiddenStateMS = Date.now() - (this.state?.hiddenStateStartTime || Date.now());
      // Set SHOW_SESSION_TIMEOUT_MODAL if time in hidden state did not exceed the defined time window
      const showSessionTimeout = (timeInHiddenStateMS < TIMEOUT_DIALOG_TIME_WINDOW_MS);
      Settings.set("SHOW_SESSION_TIMEOUT_MODAL", showSessionTimeout, "session");
      
      // Cache busting on page visibility change
      // Checks if page refresh timeout has been reached and hard reload page if so
      if (this.state.refreshTimeIsOut) {
        // Hard reload page if time is out        
        this.setState({refreshTimeIsOut: false});
        delay(500); // Small delay to ensure setting is stored before reload
        console.log("Browsing resumed after refresh timeout, refreshing session...");
        return hardReload(window.location.href);
      } else {
        // Reset refresh timeout if user returned to page before timeout
        clearTimeout(this.state.refreshTimeout);
      }
    } else if (document.visibilityState === "hidden") {
      console.log("Visibility state: hidden");
      // Reset refresh timeout if user returned to page before timeout
      clearTimeout(this.state.refreshTimeout);
      // Set the time when it went to hidden state
      this.setState({hiddenStateStartTime: Date.now()});

      this.state.refreshTimeout = setTimeout(() => {
        this.setState({refreshTimeIsOut: true});
      }, REFRESH_TIMEOUT_MS);
    }
  }

  _handleSystemWakeUp = async () => {
    Settings.set("SHOW_SESSION_TIMEOUT_MODAL", false, "session");
    await delay(2000); // Wait 2 secs to make sure session setting is set and an internet connection is established
    console.log("System woke up from sleep, refreshing session...");
    hardReload(window.location.href);
  }

  _getCustomers = async (advisorSsn, userId) => {
		const {setAppState} = this.props.actions;
		const {reducers} = this.props;
		const {customers} = reducers.app;

    if (customers || this?.fetchingCustomers) return;
    
    try
    {
      this.fetchingCustomers = true;
      const customers = await api("/advisor/clients", {"ssn": advisorSsn, "userId" : userId});
      // sort alphabetically
      customers.sort((a, b) => {
		  const aName = a.name === null ? "" : a.name;
		  const bName = b.name === null ? "" : b.name;
		  return aName.localeCompare(bName, "sv", { sensitivity: "base" })
      });
      await setAppState({customers});      
      return this.fetchingCustomers = false;
    }
    catch (e)
    {
      console.error(e.message);
    }
  }

	_closeErrorModal = async () =>
	{
		const {setAppState} = this.props.actions;
		await setAppState({
      errorModalVisible: false,
      isSessionTimeout: false,
    });
		setTimeout(() => setAppState({errorModalMessage: null}), 300);

    this.setState(
      {
        refreshTimeout: null,
        refreshTimeIsOut: false,
        hiddenStateStartTime: null,
      },
    )
    Settings.set("SHOW_SESSION_TIMEOUT_MODAL", false, "session");
	};

	_onRestartModal = async () =>
	{
		const {setAppState} = this.props.actions;
		const {reducers} = this.props;
		const {customer} = reducers.app;

		await setAppState({busy: false, errorModalVisible: false, signModalVisible: false, errorModalMessage: null});

		customer.resetCustomer();
		Settings.remove("CASE_ID");

		const {authToken, newCaseId} = await api("auth/clean-token");
		Settings.set("AUTH_TOKEN", authToken);
		customer._caseId = newCaseId;

		window.location.reload();
		Settings.set("LAST_VIEW", "1");
	};

  _onCurrentAssistantAdvisorClick = async () =>
  {
    const {reducers, actions} = this.props;
    const { app } = reducers;
    const {setAppState} = actions;
    setAppState({
      ...app,
      showSwitchAdvisorDialog: true
    });
  }
});



// PRIVATE FUNCTIONS
function _convertErrorMessage(message)
{
	if (message === "REQUEST_TIMED_OUT")
	{
		return "Anropet tog för lång tid. Var god försök igen.";
	}

	return message;
}

export default leApp;